import { type Editor, getHTMLFromFragment } from '@tiptap/core'
import { getAiToolkit } from '@tiptap-pro/ai-toolkit'
import { type RegisteredTiptapEditor } from '@opallabs/text-editor'
import { type TextEditorDocument, type ThreadInfo } from './types'

/**
 * The HTML of the editor's current selection, or null when nothing is selected.
 */
const getSelectionHTML = (editor: Editor): string | null => {
  const { from, to, empty } = editor.state.selection
  if (empty) return null

  return getHTMLFromFragment(
    editor.state.doc.slice(from, to).content,
    editor.schema
  )
}

/**
 * The document's threads in the shape the Gem AI agent payload expects.
 */
const getDocumentThreads = (entry: RegisteredTiptapEditor): ThreadInfo[] =>
  entry.readThreads().map((thread) => ({
    anchored_text: thread.anchoredText,
    comments: thread.comments.map((comment) => ({
      author: comment.author,
      text: comment.text,
    })),
  }))

/**
 * The identity of a set of open documents: which documents, and whether each
 * one can be edited. Content changes are deliberately not part of it — the
 * iframe re-posts on every keystroke, and a message snapshots the content
 * anyway — but an editability flip must reach the agent, which otherwise keeps
 * answering "that document is read-only" after the user starts editing.
 */
export const editorSetKey = (documents: TextEditorDocument[]): string =>
  documents
    .map(
      (document) => `${document.document_id}:${document.is_editable ? 1 : 0}`
    )
    .sort()
    .join(',')

/**
 * Records one editor bridge's report. Every post on a legacy moment page
 * mounts its own bridge, and each one reports only the documents it holds, so
 * a report (or the empty one a bridge sends as it goes) replaces that
 * bridge's documents and no one else's. A report with no `bridge_id` comes
 * from an iframe bundle older than the id, and counts as one bridge.
 */
export const updateBridgeDocuments = (
  bridges: ReadonlyMap<string, TextEditorDocument[]>,
  {
    bridge_id = '',
    documents,
  }: { bridge_id?: string; documents: TextEditorDocument[] }
): Map<string, TextEditorDocument[]> => {
  const next = new Map(bridges)
  if (documents.length) next.set(bridge_id, documents)
  else next.delete(bridge_id)
  return next
}

const bridgeDocumentRank = ({ is_editable, is_focused }: TextEditorDocument) =>
  (is_editable ? 2 : 0) + (is_focused ? 1 : 0)

/**
 * All the bridges' documents, one copy of each. A collapsed post card holds
 * its document read-only while the open post edits it, so the editable copy
 * wins, then the focused one.
 */
export const mergeBridgeDocuments = (
  bridges: ReadonlyMap<string, TextEditorDocument[]>
): TextEditorDocument[] => {
  const byId = new Map<string, TextEditorDocument>()
  Array.from(bridges.values())
    .flat()
    .forEach((document) => {
      const kept = byId.get(document.document_id)
      if (!kept || bridgeDocumentRank(document) > bridgeDocumentRank(kept)) {
        byId.set(document.document_id, document)
      }
    })
  return Array.from(byId.values())
}

/**
 * Serializes registered editors into the documents payload sent to the agent
 */
export const buildTextEditorDocuments = (
  editors: RegisteredTiptapEditor[]
): TextEditorDocument[] =>
  editors.map((entry) => ({
    document_id: entry.documentId,
    // The entity the document belongs to. Without it the agent sees a numeric
    // post id in the entity header and a document UUID here with nothing
    // linking them, and guesses which one a document tool wants.
    resource_id: entry.resource?.id,
    name: entry.resource?.name,
    document_content: entry.editor.getHTML(),
    selected_content: getSelectionHTML(entry.editor),
    is_editable: entry.editor.isEditable,
    is_focused: entry.isLastFocused(),
    pending_suggestions: getAiToolkit(entry.editor).getSuggestions().length,
    threads: getDocumentThreads(entry),
  }))
